/**
 * Reset Password Page
 * 
 * This page handles the password reset flow after user clicks the link in their email.
 * When the user arrives on this page, Supabase's client has already processed the 
 * access token from the URL and established a session. We use this session to 
 * update the user's password.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, AlertCircle, CheckCircle2, Eye, EyeOff, Check, X } from 'lucide-react';
import logo from '../assets/new-logo-wide.png';

// Password requirement interface
interface PasswordRequirement {
  id: string;
  label: string;
  validator: (password: string) => boolean;
}

// Password requirements configuration
const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    id: 'length',
    label: 'At least 8 characters',
    validator: (password: string) => password.length >= 8,
  },
  {
    id: 'uppercase',
    label: 'One uppercase letter (A-Z)',
    validator: (password: string) => /[A-Z]/.test(password),
  },
  {
    id: 'lowercase',
    label: 'One lowercase letter (a-z)',
    validator: (password: string) => /[a-z]/.test(password),
  },
  {
    id: 'number',
    label: 'One number (0-9)',
    validator: (password: string) => /[0-9]/.test(password),
  },
  {
    id: 'special',
    label: 'One special character (!@#$%^&*...)',
    validator: (password: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password),
  },
];

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  // Check which requirements are met
  const requirementStatus = useMemo(() => {
    return PASSWORD_REQUIREMENTS.map((req) => ({
      ...req,
      met: req.validator(newPassword),
    }));
  }, [newPassword]);

  // Check if all requirements are met
  const allRequirementsMet = useMemo(() => {
    return requirementStatus.every((req) => req.met);
  }, [requirementStatus]);

  // Calculate password strength (0-100)
  const passwordStrength = useMemo(() => {
    const metCount = requirementStatus.filter((req) => req.met).length;
    return (metCount / PASSWORD_REQUIREMENTS.length) * 100;
  }, [requirementStatus]);

  // Get strength label and color
  const strengthInfo = useMemo(() => {
    if (newPassword.length === 0) return { label: '', color: 'bg-gray-200' };
    if (passwordStrength < 40) return { label: 'Weak', color: 'bg-red-500' };
    if (passwordStrength < 80) return { label: 'Medium', color: 'bg-yellow-500' };
    return { label: 'Strong', color: 'bg-green-500' };
  }, [newPassword, passwordStrength]);

  // Check if we have a valid session from the recovery link
  useEffect(() => {
    const checkSession = async () => {
      try {
        console.log('[ResetPasswordPage] Checking for recovery session...');
        
        // Give Supabase a moment to process the URL hash
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[ResetPasswordPage] Session error:', error);
          setHasValidSession(false);
        } else if (session) {
          console.log('[ResetPasswordPage] Valid session found for:', session.user?.email);
          setHasValidSession(true);
          setUserEmail(session.user?.email || null);
        } else {
          console.log('[ResetPasswordPage] No session found');
          setHasValidSession(false);
        }
      } catch (err) {
        console.error('[ResetPasswordPage] Error checking session:', err);
        setHasValidSession(false);
      } finally {
        setCheckingSession(false);
      }
    };

    checkSession();

    // Also listen for auth state changes (in case Supabase processes the token after mount)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      console.log('[ResetPasswordPage] Auth state change:', event);
      if (event === 'PASSWORD_RECOVERY') {
        console.log('[ResetPasswordPage] Password recovery event detected');
        setHasValidSession(true);
        setCheckingSession(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate all password requirements
    if (!allRequirementsMet) {
      setError('Please ensure your password meets all requirements');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      console.log('[ResetPasswordPage] Updating password via Supabase...');
      
      // Use Supabase client to update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        console.error('[ResetPasswordPage] Password update error:', updateError);
        throw new Error(updateError.message);
      }

      console.log('[ResetPasswordPage] Password updated successfully!');
      
      // Sign out after password update so user logs in with new password
      await supabase.auth.signOut();
      
      setSuccess(true);
    } catch (error) {
      console.error('Reset password error:', error);
      setError((error as Error).message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while checking session
  if (checkingSession) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black p-4">
        <div className="flex flex-col items-center mb-6">
          <div className="h-16 w-16 rounded-full bg-black flex items-center justify-center shadow-md mb-2">
            <img src={logo} alt="Recon logo" className="h-12 w-12 object-contain" />
          </div>
        </div>
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Verifying reset link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No valid session state
  if (!hasValidSession) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black p-4">
        <div className="flex flex-col items-center mb-6">
          <div className="h-16 w-16 rounded-full bg-black flex items-center justify-center shadow-md mb-2">
            <img src={logo} alt="Recon logo" className="h-12 w-12 object-contain" />
          </div>
        </div>
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center">Invalid Reset Link</CardTitle>
            <CardDescription className="text-center">
              This password reset link is invalid or has expired. Please request a new one.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex flex-col space-y-2">
            <Button
              className="w-full"
              onClick={() => navigate('/forgot-password')}
            >
              Request New Reset Link
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/login')}
            >
              Back to Login
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black p-4">
        <div className="flex flex-col items-center mb-6">
          <div className="h-16 w-16 rounded-full bg-black flex items-center justify-center shadow-md mb-2">
            <img src={logo} alt="Recon logo" className="h-12 w-12 object-contain" />
          </div>
        </div>
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center">Password Reset Successful</CardTitle>
            <CardDescription className="text-center">
              Your password has been updated successfully. You can now log in with your new password.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              className="w-full"
              onClick={() => navigate('/login', { state: { email: userEmail } })}
            >
              Continue to Login
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black p-4">
      <div className="flex flex-col items-center mb-6">
        <img src={logo} alt="Recon logo" className="w-48 mb-2" />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Reset Your Password</CardTitle>
          <CardDescription>
            Enter your new password below.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-md border border-red-200 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <p>{error}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (error) setError(null);
                  }}
                  onFocus={() => setShowPasswordRequirements(true)}
                  disabled={loading}
                  required
                  autoComplete="new-password"
                  autoFocus
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              
              {/* Password strength bar */}
              {newPassword.length > 0 && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Password strength</span>
                    <span className={`font-medium ${
                      strengthInfo.label === 'Weak' ? 'text-red-600' :
                      strengthInfo.label === 'Medium' ? 'text-yellow-600' :
                      strengthInfo.label === 'Strong' ? 'text-green-600' : ''
                    }`}>
                      {strengthInfo.label}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${strengthInfo.color}`}
                      style={{ width: `${passwordStrength}%` }}
                    />
                  </div>
                </div>
              )}
              
              {/* Password requirements checklist */}
              {showPasswordRequirements && (
                <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                  <p className="text-xs font-medium text-gray-700 mb-2">Password must contain:</p>
                  <ul className="space-y-1">
                    {requirementStatus.map((req) => (
                      <li 
                        key={req.id}
                        className={`flex items-center gap-2 text-xs ${
                          req.met ? 'text-green-600' : 'text-gray-500'
                        }`}
                      >
                        {req.met ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : (
                          <X className="h-3.5 w-3.5" />
                        )}
                        <span>{req.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (error) setError(null);
                  }}
                  disabled={loading}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {/* Password match indicator */}
              {confirmPassword.length > 0 && (
                <div className={`flex items-center gap-1 text-xs ${
                  newPassword === confirmPassword ? 'text-green-600' : 'text-red-600'
                }`}>
                  {newPassword === confirmPassword ? (
                    <>
                      <Check className="h-3.5 w-3.5" />
                      <span>Passwords match</span>
                    </>
                  ) : (
                    <>
                      <X className="h-3.5 w-3.5" />
                      <span>Passwords do not match</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter className="mt-4">
            <Button
              type="submit"
              className="w-full"
              disabled={loading || !allRequirementsMet || newPassword !== confirmPassword}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};
